import memoizeOne from 'memoize-one';
import { graphql } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import type {
	preloadRovoChatIdentityQuery$data,
	preloadRovoChatIdentityQuery$variables,
} from './__generated__/preloadRovoChatIdentityQuery.graphql.ts';

const RovoChatIdentityQuery = graphql`
	query preloadRovoChatIdentityQuery($cloudId: String!) {
		agentStudio_getAgents(
			cloudId: $cloudId
			product: "confluence"
			first: 1
			input: { creatorTypes: [ROVO_CHAT] }
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					identityAccountId
				}
			}
		}
	}
`;

// Keep only the last site's query metadata; identity data is always read from Relay.
const getRovoChatIdentityOperation = memoizeOne((cloudId: string) =>
	createOperationDescriptor(getRequest(RovoChatIdentityQuery), { cloudId }),
);

export const preloadRovoChatIdentity = async (cloudId: string): Promise<string | undefined> => {
	const { data, errors, hasErrors } = await queryAgg<
		preloadRovoChatIdentityQuery$data,
		preloadRovoChatIdentityQuery$variables
	>({
		query: RovoChatIdentityQuery,
		variables: { cloudId },
		// Keep identity available after the view query unmounts and Relay runs GC.
		relayQueryPreloadRetention: 'environment',
	});

	if (hasErrors) {
		throw new Error('Failed to query Rovo mention identity', { cause: errors?.[0] });
	}

	return getRovoChatIdentity(data);
};

/** Reads preloaded identity data without starting a request. Null means a completed empty lookup. */
export function getRovoChatIdentityFromCache(cloudId: string): string | null | undefined {
	const environment = window.GLOBAL_RELAY_ENV;
	if (!environment) {
		return undefined;
	}
	const operation = getRovoChatIdentityOperation(cloudId);
	const snapshot = environment.lookup(operation.fragment);
	const data = snapshot.isMissingData
		? undefined
		: (snapshot.data as preloadRovoChatIdentityQuery$data);
	// A failed lookup can cache a null connection; allow the async provider to retry it.
	return data?.agentStudio_getAgents ? (getRovoChatIdentity(data) ?? null) : undefined;
}

function getRovoChatIdentity(
	data: Partial<preloadRovoChatIdentityQuery$data> | null | undefined,
): string | undefined {
	return normalizeAccountIdFromIdentityAri(
		data?.agentStudio_getAgents?.edges?.[0]?.node?.identityAccountId,
	);
}
